// Property context per email message id (internetMessageId), persisted for the browser session.
// Structure: { [messageId: string]: { propertyId: number, text: string, instruction?: string } }
const contextCache = {};
export const INSTRUCTION_STORE_KEY = "instructionDrafts";
export const PROPERTY_CONTEXT_STORE_KEY = "propertyContextByMessage";

const readInstructionStore = () => {
  try {
    const raw = localStorage.getItem(INSTRUCTION_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const writeInstructionStore = (map) => {
  try {
    localStorage.setItem(INSTRUCTION_STORE_KEY, JSON.stringify(map || {}));
  } catch {
    // ignore
  }
};

const readPropertyContextStore = () => {
  try {
    const raw = localStorage.getItem(PROPERTY_CONTEXT_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const writePropertyContextStore = (map) => {
  try {
    localStorage.setItem(PROPERTY_CONTEXT_STORE_KEY, JSON.stringify(map || {}));
  } catch {
    // ignore
  }
};

export const getCachedContext = (messageId) => {
  if (!messageId) return undefined;
  if (contextCache[messageId]) {
    return contextCache[messageId];
  }
  const store = readPropertyContextStore();
  const stored = store[messageId];
  if (stored) {
    contextCache[messageId] = { ...stored };
    return contextCache[messageId];
  }
  return undefined;
};

export const hasCachedPropertyContext = (messageId) => getCachedContext(messageId) !== undefined;

export const setCachedContext = (messageId, entry) => {
  if (!messageId) return;
  const existing = contextCache[messageId] || {};
  contextCache[messageId] = {
    // Merge to preserve previously stored fields (e.g., instruction)
    ...existing,
    ...(typeof entry === "object" && entry !== null ? entry : {}),
    propertyId:
      typeof entry?.propertyId === "string"
        ? parseInt(entry.propertyId, 10)
        : (entry?.propertyId ?? existing.propertyId ?? 0),
    text: entry?.text !== undefined ? entry.text : (existing.text || ""),
  };
  const store = readPropertyContextStore();
  store[messageId] = {
    propertyId: contextCache[messageId].propertyId ?? 0,
    text: contextCache[messageId].text || "",
  };
  writePropertyContextStore(store);
  try {
    console.log("Context cache update:", { messageId, entry: contextCache[messageId] });
  } catch (e) {
    // ignore
  }
};

export const clearCachedContext = (messageId) => {
  if (!messageId) return;
  delete contextCache[messageId];
  const store = readPropertyContextStore();
  if (messageId in store) {
    delete store[messageId];
    writePropertyContextStore(store);
  }
  try {
    console.log("Context cache cleared:", { messageId });
  } catch (e) {
    // ignore
  }
};

export const getCachedInstruction = (messageId) => {
  if (!messageId) return "";
  const entry = contextCache[messageId];
  if (entry && typeof entry.instruction === "string") {
    return entry.instruction || "";
  }
  // Fallback to persistent store
  const store = readInstructionStore();
  const val = store[messageId];
  if (typeof val === "string") {
    // hydrate memory for faster subsequent access
    const existing = contextCache[messageId] || {};
    contextCache[messageId] = { ...existing, instruction: val };
    return val;
  }
  return "";
};

export const setCachedInstruction = (messageId, instruction) => {
  if (!messageId) return;
  const existing = contextCache[messageId] || {};
  contextCache[messageId] = { ...existing, instruction: instruction || "" };
  // Persist to localStorage to survive WebView reloads/module switches
  const store = readInstructionStore();
  store[messageId] = instruction || "";
  writeInstructionStore(store);
  try {
    console.log("Instruction cache update:", { messageId, instruction });
  } catch (e) {
    // ignore
  }
};

export const clearCachedInstruction = (messageId) => {
  if (!messageId) return;
  const existing = contextCache[messageId] || {};
  if (existing) {
    delete existing.instruction;
    if (Object.keys(existing).length > 0) {
      contextCache[messageId] = existing;
    } else {
      delete contextCache[messageId];
    }
  }
  const store = readInstructionStore();
  if (messageId in store) {
    delete store[messageId];
    writeInstructionStore(store);
  }
  try {
    console.log("Instruction cache cleared:", { messageId });
  } catch (e) {
    // ignore
  }
};
